import { Avatar } from "@/components/fornada/brand";
import { ConfirmSheet } from "@/components/fornada/sheet";
import { EmptyState, ScreenTitle, Surface } from "@/components/fornada/primitives";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { ContentLoader } from "@/components/ui/loaders";
import { useResetUserPassword, useUsers, type Seller } from "@/hooks/use-users";
import { initials } from "@/lib/format";
import { useAuth } from "@/providers/auth.provider";
import { useToast } from "@/providers/toast.provider";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Copy, KeyRound } from "lucide-react";
import { useState } from "react";
import { Navigate } from "react-router-dom";

/** Senha temporária: só aparece aqui uma vez — fechou, só redefinindo de novo. */
function TempPasswordSheet({
  seller,
  password,
  onClose,
}: {
  seller?: Seller;
  password: string | null;
  onClose: () => void;
}) {
  const { toaster } = useToast();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!password) return;
    await navigator.clipboard.writeText(password);
    setCopied(true);
    toaster("Senha copiada!", "success");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={!!password} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="top-auto bottom-0 left-0 max-w-none translate-x-0 translate-y-0 gap-5 rounded-b-none rounded-t-sheet border-fx-line bg-fx-surface p-5 pb-[max(20px,env(safe-area-inset-bottom))] data-[state=open]:slide-in-from-bottom data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100 sm:top-1/2 sm:bottom-auto sm:left-1/2 sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-hero sm:p-6"
      >
        <div className="flex flex-col gap-2">
          <DialogTitle className="fx-display text-[21px] leading-[22px] tracking-[-0.025em] text-fx-title">
            Senha temporária gerada
          </DialogTitle>
          <DialogDescription className="text-sm leading-5 text-fx-secondary">
            Envie esta senha para {seller?.name ?? "o vendedor"} — ela só aparece aqui
            uma vez. No próximo login, será pedida uma nova senha.
          </DialogDescription>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-fx-line bg-fx-input py-1.5 pr-1.5 pl-4">
          <span className="flex-1 font-mono text-lg tracking-[1px] text-fx-title select-all">
            {password}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={copy}
            aria-label="Copiar senha"
            className={copied ? "text-fx-cream" : undefined}
          >
            {copied ? <Check /> : <Copy />}
          </Button>
        </div>
        <Button size="lg" onClick={onClose}>
          Fechar
        </Button>
      </DialogContent>
    </Dialog>
  );
}

const SellersPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const queryClient = useQueryClient();
  const { data: sellers = [], isLoading } = useUsers(isAdmin);
  const { mutate: resetPassword, isPending } = useResetUserPassword();
  const [resetting, setResetting] = useState<Seller | null>(null);
  const [result, setResult] = useState<{ seller: Seller; password: string } | null>(null);

  if (!isAdmin) return <Navigate to="/" replace />;
  if (isLoading) return <ContentLoader show />;

  const confirmReset = () => {
    if (!resetting) return;
    const seller = resetting;
    resetPassword(seller.id, {
      onSuccess: ({ password }) => {
        setResult({ seller, password });
        queryClient.invalidateQueries({ queryKey: ["users"] });
      },
      onSettled: () => setResetting(null),
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <ScreenTitle
        title="Vendedores"
        subtitle="Quem esqueceu a senha recebe uma temporária e troca no próximo login."
      />

      {sellers.length > 0 ? (
        <Surface className="divide-y divide-fx-line-subtle">
          {sellers.map((s) => (
            <div key={s.id} className="flex items-center gap-3 px-4 py-3.5 md:px-5">
              <Avatar initials={initials(s.name)} />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex items-center gap-2">
                  <span className="truncate text-[14.5px] font-semibold text-fx-title">
                    {s.name}
                  </span>
                  {s.role === "ADMIN" && (
                    <span className="text-[9.5px] font-bold tracking-[1.3px] text-fx-muted">
                      ADMIN
                    </span>
                  )}
                </span>
                <span className="truncate text-[12.5px] text-fx-muted">{s.email}</span>
                {s.needToChangePassword && (
                  <span className="text-[11.5px] font-medium text-fx-red-ink">
                    Aguardando troca de senha
                  </span>
                )}
              </div>
              {s.id !== user?.id && (
                <Button
                  variant="quiet"
                  size="sm"
                  onClick={() => setResetting(s)}
                  aria-label={`Redefinir senha de ${s.name}`}
                >
                  <KeyRound />
                  <span className="hidden sm:inline">Redefinir senha</span>
                </Button>
              )}
            </div>
          ))}
        </Surface>
      ) : (
        <EmptyState
          title="Nenhum vendedor"
          description="Quem criar uma conta aparece aqui."
        />
      )}

      <ConfirmSheet
        open={!!resetting}
        onOpenChange={(o) => !o && setResetting(null)}
        title={`Redefinir senha de ${resetting?.name ?? ""}?`}
        description="Uma senha temporária será gerada e a senha atual deixa de funcionar. No próximo login, será pedida uma nova senha."
        confirmLabel="Redefinir senha"
        pending={isPending}
        onConfirm={confirmReset}
      />

      <TempPasswordSheet
        seller={result?.seller}
        password={result?.password ?? null}
        onClose={() => setResult(null)}
      />
    </div>
  );
};

export default SellersPage;
